import React, { useRef } from "react";
import { CSV } from "../../../core/theme/searchTheme";
import { CSV_COLUMNS, MAX_CSV_LINES, templateCsv, samplesCode, spacingText } from "../../../core/data/csvScans";
import SearchTabs from "./SearchTabs";

// The Upload CSV tab: download the template, choose a file, and see every
// line as the snapshot it will make before anything runs.
export default function CsvUpload({ form, upload, onOpenScans }) {
  const fileInput = useRef(null);
  const { file, planned, totals, lookingUp, runState } = upload;
  const busy = runState === "running";

  const run = () => {
    const s = totals.ready === 1 ? "" : "s";
    if (window.confirm(`Start ${totals.ready} snapshot${s} (${totals.searches.toLocaleString("en-GB")} searches)? Lines with problems are left out.`)) {
      upload.runAll();
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([templateCsv()], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "adaudit-snapshots-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const choose = (e) => {
    upload.loadFile(e.target.files && e.target.files[0]);
    e.target.value = "";
  };

  return (
    <div className={CSV.page}>
      <div className={CSV.top}>
        <SearchTabs mode={form.mode} setMode={form.setMode} />
        <p className={CSV.intro}>
          One line = one snapshot, up to {MAX_CSV_LINES}. Columns: {CSV_COLUMNS.map(c => <span key={c} className={CSV.code}>{c}</span>).reduce((a, b) => [a, " ", b])}.
          Separate several locations, keywords or tags with <span className={CSV.code}>;</span>.
          Each location gets the radius's 5 picks. Samples is a code like <span className={CSV.code}>3x1hour</span>,{" "}
          <span className={CSV.code}>5x1min</span>, <span className={CSV.code}>3x15min</span> or{" "}
          <span className={CSV.code}>10x1day</span>; blank = 3 every hour. Each line needs keywords and at least one
          postcode; everything else is optional. Nothing runs until you've checked the preview.
        </p>
        <div className={CSV.actions}>
          <button type="button" className={CSV.buttonQuiet} onClick={downloadTemplate}>Download template</button>
          <button type="button" className={CSV.button} disabled={busy} onClick={() => fileInput.current && fileInput.current.click()}>
            {file ? "Choose another file" : "Choose CSV file"}
          </button>
          <input ref={fileInput} type="file" accept=".csv,text/csv" className="hidden" onChange={choose} />
          {file && <span className={CSV.fileName}>{file.name}</span>}
          {file && <button type="button" className={CSV.buttonQuiet} disabled={busy} onClick={upload.clear}>Clear</button>}
        </div>
      </div>

      <div className={CSV.scroll}>
        {!file && <p className={CSV.empty}>Choose a CSV file to see its snapshots here.</p>}
        {file && file.error && <p className={CSV.fileError}>{file.error}</p>}
        {lookingUp && <p className={CSV.empty}>Looking up postcodes…</p>}
        {planned && <PreviewTable planned={planned} results={upload.results} />}
      </div>

      {totals && (
        <div className={CSV.footer}>
          {runState === "idle" ? (
            <span className={CSV.totals}>
              <span className={CSV.strong}>{totals.ready}</span> of {totals.lines} snapshot{totals.lines === 1 ? "" : "s"} ready
              {" · "}<span className={CSV.strong}>{totals.searches.toLocaleString("en-GB")}</span> searches
              {totals.blocked > 0 && <> · {totals.blocked} with problems (left out)</>}
              {totals.flagged > 0 && <> · {totals.flagged} share a website</>}
            </span>
          ) : (
            <span className={CSV.totals}>
              {busy ? "Starting snapshots… " : "Done: "}
              <span className={CSV.strong}>{upload.started}</span> of {totals.ready} started
              {upload.failed > 0 && <> · <span className="text-red-700">{upload.failed} couldn't start</span></>}
              {!busy && <> · each starts within a minute; big uploads queue, so later samples may run a little late</>}
            </span>
          )}
          <div className={CSV.actions}>
            {runState === "idle" && (
              <button type="button" className={CSV.runButton} disabled={totals.ready === 0} onClick={run}>
                Start {totals.ready} snapshot{totals.ready === 1 ? "" : "s"}
              </button>
            )}
            {runState === "done" && (
              <button type="button" className={CSV.runButton} onClick={onOpenScans}>Go to Audits</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PreviewTable({ planned, results }) {
  return (
    <table className={CSV.table}>
      <thead>
        <tr>
          <th className={CSV.th}>Line</th>
          <th className={CSV.th}>Business</th>
          <th className={CSV.th}>Postcodes</th>
          <th className={CSV.th}>Keywords</th>
          <th className={CSV.th}>Samples</th>
          <th className={CSV.thNumber}>Searches</th>
          <th className={CSV.th}>Status</th>
        </tr>
      </thead>
      <tbody>
        {planned.map(p => <PreviewRow key={p.line.lineNumber} p={p} result={results[p.line.lineNumber]} />)}
      </tbody>
    </table>
  );
}

function PreviewRow({ p, result }) {
  const { line, plan, errors, noPicks, ready } = p;
  const picks = plan.postcodes.filter(x => !x.isExtra);
  return (
    <tr className={ready ? "" : CSV.rowBlocked}>
      <td className={CSV.td}>{line.lineNumber}</td>
      <td className={CSV.td}>
        {line.website
          ? <div className={CSV.strong}>{line.businessName || line.domain || line.website}</div>
          : <div className={CSV.muted}>No business</div>}
        {line.businessName && line.domain && <div className={CSV.muted}>{line.domain}</div>}
        {line.website && line.tags.length > 0 && <div>{line.tags.map(t => <span key={t} className={CSV.tag}>{t}</span>)}</div>}
        {line.sameWebsiteAs.length > 0 && (
          <div className={CSV.flag}>Same website as line {line.sameWebsiteAs.join(", ")} - a separate snapshot</div>
        )}
      </td>
      <td className={CSV.td}>
        <div>
          <span className={CSV.strong}>{plan.postcodes.length}</span>
          {line.radius && line.locations.length > 0
            ? <span className={CSV.muted}> · {line.radius} mi radius round {line.locations.join(", ")}</span>
            : <span className={CSV.muted}> · no radius</span>}
        </div>
        <div>{picks.map(x => <span key={x.postcode} className={CSV.code2}>{x.postcode}</span>)}</div>
        {plan.added.length > 0 && <div><span className={CSV.muted}>Added: </span>{plan.added.map(x => x.postcode).join(", ")}</div>}
        {plan.skipped.length > 0 && <div className={CSV.muted}>Already picked: {plan.skipped.map(x => x.postcode).join(", ")}</div>}
        {plan.gaps.length > 0 && (
          <div className={CSV.muted}>No district found: {plan.gaps.map(g => `${g.quarter} of ${g.centre}`).join(", ")}</div>
        )}
        {noPicks.length > 0 && <div className={CSV.muted}>No radius picks for {noPicks.join(", ")}</div>}
      </td>
      <td className={CSV.td}>
        <div>{line.keywords.join(", ") || "—"}</div>
        {plan.runNiche && <div className={CSV.muted}>{plan.runNiche.name}</div>}
      </td>
      <td className={CSV.td}>
        <div>{samplesCode(line.samples, line.spacing)}</div>
        <div className={CSV.muted}>{line.samples} sample{line.samples === 1 ? "" : "s"} {spacingText(line.spacing)}</div>
      </td>
      <td className={CSV.tdNumber}>{ready ? plan.total.toLocaleString("en-GB") : "—"}</td>
      <td className={CSV.td}>
        {!ready && <ul className={CSV.errorList}>{errors.map(e => <li key={e}>{e}</li>)}</ul>}
        {ready && !result && <span className={CSV.ready}>Ready</span>}
        {result && result.status === "starting" && <span className={CSV.muted}>Starting…</span>}
        {result && result.status === "started" && <span className={CSV.ready}>Started ✓</span>}
        {result && result.status === "failed" && <div className="text-red-700">Couldn't start: {result.error}</div>}
        {result && result.tagError && <div className={CSV.flag}>Started, but tags weren't added: {result.tagError}</div>}
        {line.notes.map(n => <div key={n} className={CSV.flag}>{n}</div>)}
      </td>
    </tr>
  );
}
